import { useState } from "react";

import { Box, FormControlLabel, Typography } from "@mui/material";
import { useTheme } from "@mui/material";
import Switch from '@mui/material/Switch';

import { ComposedChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Bar } from "recharts";
import { DataLayout } from "./DataLayout";
import { formatCurrency } from "../utils";

export const BudgetChart = ({ budgetData }: { budgetData: any[] }) => {
    const theme = useTheme();
    const [cumulative, setCumulative] = useState(false);
    const [selectedBudgets, setSelectedBudgets] = useState<string[]>([]);

    if (!budgetData || budgetData.length === 0) return null;

    // All budget keys dynamically
    const BUDGET_KEYS = Object.keys(budgetData[0]).filter((k) =>
        k.startsWith("Budget")
    );

    // Line chart keys
    const LINE_KEYS = [
        { key: "Actual", color: "#1E90FF", order: 1 },
        { key: "Forecast", color: "#32CD32", order: 2 },
        { key: "Baseline", color: "#FFA500", order: 3 },
        { key: "EAC", color: "#8A2BE2", order: 4 },
        { key: "Commitments", color: "#FF1493", order: 5 },
        { key: "Revised", color: "#00CED1", order: 6 },
        { key: "Variation", color: "#FF4500", order: 7 },
    ];

    // Toggle legend selection
    const toggleBudget = (key: string) => {
        setSelectedBudgets((prev) => {
            if (prev.includes(key)) {
                return prev.filter((k) => k !== key); // deselect
            } else {
                return [...prev, key]; // select
            }
        });
    };

    // If no selection → show all budgets
    const visibleBudgets =
        selectedBudgets.length === 0 ? BUDGET_KEYS : selectedBudgets;

    return (
        <DataLayout
            title="Project S-Curve & Cash Flow Forecast"
            action={
                <FormControlLabel
                    control={
                        <Switch
                            checked={cumulative}
                            onChange={() => setCumulative(!cumulative)}
                        />
                    }
                    label="Cumulative"
                />
            }
        >
            <Box py={3}>
                <ResponsiveContainer width="100%" height={330}>
                    <ComposedChart
                        data={budgetData}
                        margin={{ top: 20, right: 70 }}
                    >
                        <CartesianGrid stroke={theme.palette.divider} />
                        <XAxis dataKey="month" />
                        <YAxis tickFormatter={(value) => formatCurrency(value)} domain={["auto", "auto"]} />

                        {/* BUDGET BARS */}
                        {BUDGET_KEYS.map((key, idx) => {
                            const isVisible = visibleBudgets.includes(key);
                            const color =
                                theme.palette.secondary[
                                ["light", "main", "dark"][idx % 3] as keyof typeof theme.palette.secondary
                                ];

                            return (
                                <Bar
                                    key={key}
                                    dataKey={key}
                                    stackId="budget"
                                    fill={isVisible ? color : "#d3d3d3"}
                                    hide={!isVisible}
                                />
                            );
                        })}

                        {/* LINES */}
                        {LINE_KEYS.sort((a, b) => a.order - b.order).map(
                            ({ key, color }) => (
                                <Line
                                    key={key}
                                    type="monotone"
                                    dataKey={`${key}.${cumulative ? "cumulative" : "value"}`}
                                    stroke={color}
                                    strokeWidth={2}
                                    dot={false}
                                />
                            )
                        )}
                    </ComposedChart>
                </ResponsiveContainer>

                {/* LEGEND */}
                <Box display="flex" flexWrap="wrap" gap={2} mt={2} px={3}>
                    {/* Budget Legends */}

                    {/* Line Legends */}
                    {LINE_KEYS.map(({ key, color }) => (
                        <Box
                            key={key}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                            }}
                        >
                            <Box
                                sx={{
                                    width: 22,
                                    height: 3,
                                    borderRadius: 2,
                                    backgroundColor: color,
                                }}
                            />
                            <Typography>{key}</Typography>
                        </Box>
                    ))}

                </Box>
                <Box display="flex" flexWrap="wrap" gap={2} mt={2} px={3}>


                    {BUDGET_KEYS.map((key, idx) => {
                        const isSelected = selectedBudgets.includes(key);
                        const color =
                            theme.palette.secondary[
                            ["light", "main", "dark"][idx % 3] as keyof typeof theme.palette.secondary
                            ];

                        return (
                            <Box
                                key={key}
                                onClick={() => toggleBudget(key)}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    cursor: "pointer",
                                    opacity:
                                        isSelected ||
                                            selectedBudgets.length === 0
                                            ? 1
                                            : 0.4,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 16,
                                        height: 16,
                                        borderRadius: "4px",
                                        backgroundColor:
                                            isSelected ||
                                                selectedBudgets.length === 0
                                                ? color
                                                : "#bfbfbf",
                                        border: "1px solid #999",
                                    }}
                                />
                                <Typography>{key}</Typography>
                            </Box>
                        );
                    })}


                </Box>
            </Box>
        </DataLayout>
    );
};

const LegendItem = ({ color, label, line = false }: any) => (
    <Box display="flex" alignItems="center" gap={1}>
        {line ? (
            <Box width={20} height={2} bgcolor={color} />
        ) : (
            <Box width={14} height={14} borderRadius={2} bgcolor={color} />
        )}
        <Typography variant="body2">{label}</Typography>
    </Box>
);